import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Grid,
  Box,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form } from "formik";
import DatePickerComponent from "../../../../../../../common/components/date-picker/date-picker-compoenent";
import {
  AlertComponent,
  TextInputComponent,
} from "../../../../../../../common/components";
import {
  CALL_LOG_DIALOG_INITIAL_VALUES,
  CALL_DIALOG_VALIDATION_SCHEMA,
  CALL_PURPOSE_OPTIONS,
  CALL_RESULT_OPTIONS,
  callTypeOptions,
  CALL_DIALOG_INITIAL_VALUES,
} from "./common-dialog-helper";
import SearchableSelectOwner from "../../../../../../../common/components/select-owner/select-owner-compoent";
import { getCurrentUser } from "../../../../../../../common/routines/user-operations";
import SelectInputComponent from "../../../../../../../common/components/select-input-component/select-input-component";
import dayjs from "dayjs";
import "./common-dialog-task.scss";
import "./common-dialog-call.scss";
import { getUserDetails } from "../../../../../../../api/lead";
import { useSetLoading } from "../../../../../../../common/hooks/appearance";
import SearchableAutoSelect from "../../../../../../../common/components/auto-select-search-component/search-select-component";
import { TimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import lockIcon from "../../../../../../../assets/icons/lock-icon.svg";
import { getCombinedDateTime } from "../../../../../../../utils/utils";
import CallDurationInput from "./call-duration-input";
import Yup from "yup";
import { ConfirmationPopup } from "../../../../../../../common/components";
import ConfirmInfo from "../../../../../../../assets/icons/CreateLeadCapturePopupCancelIcon.svg";
import MultiSelectAutocompleteWithProfile from "../../../../../../leads/lead-assignment/assignement-rules/multi-option-select-with-profile";
import {
  getAssignedPeopleList,
  createCall,
  editCall,
} from "../../../../../../../api/campaign";
import { formatDate, formatTime } from "../../../../../../../utils/utils";
import { useTheme } from "@mui/material/styles";

interface CommonCallDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: any) => void;
  title?: string;
  campaignDetails?: any;
  initialCall?: any;
  isLogCall?: boolean;
  customerOptions?: any[]; // This prop is not used in the component, but can be passed if needed
  markCallAsCompletedOpen?: boolean;
}
export interface OwnerOption {
  value: string;
  label: string;
  email: string;
  avatar?: string | null;
}
const CommonCallLogDialog: React.FC<CommonCallDialogProps> = ({
  open,
  onClose,
  onSave,
  campaignDetails,
  title = "Schedule Call",
  initialCall,
  isLogCall = true,
  customerOptions = [],
  markCallAsCompletedOpen,
}) => {
  const [leadOptions, setLeadOptions] = useState<OwnerOption[]>([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error" | "warning">(
    "success"
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [formInitialValues, setFormInitialValues] = useState(
    CALL_DIALOG_INITIAL_VALUES
  );
  const [isConflictWithMeeting, setIsConflictWithMeeting] = useState(false);
  const [conflictMessage, setConflictMessage] = useState("");
  const [isFutureCallStart, setIsFutureCallStart] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // mobile ≤ 600px

  useEffect(() => {
    setIsConflictWithMeeting(false);
  }, [open]);

  useEffect(() => {
    if (markCallAsCompletedOpen && initialCall?.call_start_time) {
      const result =
        markCallAsCompletedOpen &&
        initialCall?.call_start_time &&
        dayjs(initialCall.call_start_time).isAfter(dayjs());
      setIsFutureCallStart(result);
      if (result) {
        setAlertMessage("We can only mark calls as completed for past dates.");
        setAlertType("warning"); // or "error", depending on your design
        setAlertOpen(true);
      }
    } else setIsFutureCallStart(false);
  }, [markCallAsCompletedOpen, initialCall]);

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const fetchCallStatus = () => {
    const isOverdue = dayjs(initialCall?.call_start_time).isBefore(dayjs());
    if (markCallAsCompletedOpen) {
      return "Completed";
    }
    if (initialCall?.out_going_call_status !== "Completed" && isOverdue) {
      return "Overdue";
    }
    return initialCall?.out_going_call_status || "Completed";
  };

  const parseCallDuration = (durationStr: string | null | undefined) => {
    if (!durationStr) return null;
    const [hh = "00", mm = "00", ss = "00"] = durationStr.split(":");
    return { hh, mm, ss };
  };

  const computedInitialValues = useMemo(() => {
    let values;

    if (initialCall) {
      values = {
        callFor: initialCall.callFors || [],
        relatedTo: campaignDetails?.name || "",
        callType: initialCall.call_type || "",
        outGoingCallStatus: initialCall.outgoing_call_status
          ? fetchCallStatus()
          : "",
        callStartTime: initialCall.call_start_time
          ? dayjs(initialCall.call_start_time)
          : null,
        callStartDate: initialCall.call_start_time
          ? dayjs(initialCall.call_start_time)
          : null,
        callOwner: initialCall.callOwner?.id
          ? String(initialCall.callOwner.id)
          : "",
        subject: initialCall.subject || "",
        callAgenda: initialCall.call_agenda || "",
        callPurpose: initialCall.call_purpose || "",
        callResult: initialCall.call_results || "",
        description: initialCall.description || "",
        callDuration: initialCall.call_duration
          ? parseCallDuration(initialCall.call_duration)
          : null,
      };
    } else {
      const currentUserId = getCurrentUser();
      const matchedUser = leadOptions.find(
        (opt) => opt.value === String(currentUserId?.id)
      );

      values = {
        ...CALL_DIALOG_INITIAL_VALUES,
        callOwner: matchedUser?.value || "",
        relatedTo: campaignDetails?.name || "",
        outGoingCallStatus: "Completed",
      };
    }

    return values;
  }, [initialCall, campaignDetails, leadOptions, isLogCall]);

  const [pendingPayload, setPendingPayload] = useState<any>(null);

  const padTime = (val: string | number) => String(val).padStart(2, "0");

  const preparePayload = (values: any) => {
    const payload: any = {
      callFor: values?.callFor?.map((opt: any) => ({
        id: opt?.id,
        resource: opt?.resource,
      })),
      relatedToCampaignId: campaignDetails?.id || null,
      callType: values.callType || null,
      outGoingCallStatus: values.outGoingCallStatus,
      callStartTime: getCombinedDateTime(
        values.callStartDate,
        values.callStartTime
      ),
      ownerId: Number(values.callOwner),
      subject: values.subject || null,
      callPurpose:
        (values.callPurpose === "None" ? null : values.callPurpose) || null,
      callAgenda: values.callAgenda || null,
      notes: initialCall?.notes || null, // Use initialCall?.notes if available
      cancelNotes: initialCall?.cancel_notes || null, // Use initialCall?.cancel_notes if available
    };

    if (isLogCall || initialCall?.call_duration) {
      const { hh, mm, ss } = values.callDuration || {};
      const hasAnyDuration = hh || mm || ss;
      let durationParts: string[] = [];
      if (hasAnyDuration) {
        durationParts = [padTime(hh || 0), padTime(mm || 0), padTime(ss || 0)];
      }
      const formattedDuration: string = durationParts?.length
        ? durationParts?.join(":")
        : "";
      payload.callResult =
        (values.callResult === "None" ? null : values.callResult) || null;
      payload.description = values.description || null;
      payload.callDuration = formattedDuration || null;
    }

    return payload;
  };

  const saveCall = async (payload: any, originalValues?: any) => {
    try {
      setLoading(true);
      let response;
      if (initialCall?.id) {
        response = await editCall(initialCall.id, payload);
        setAlertMessage(
          `Call ${markCallAsCompletedOpen ? "completed" : "updated"} successfully.`
        );
      } else {
        response = await createCall(payload);
        setAlertMessage("Call created successfully.");
      }

      setAlertType("success");
      setAlertOpen(true);
      onSave(response?.data || originalValues);
      onClose();
    } catch (error: any) {
      console.error("Error saving call:", error);
      if (error?.response?.status === 409) {
        setPendingPayload(payload); // Store for re-use
        setIsConflictWithMeeting(true);
        setConflictMessage(
          error?.response?.data?.message ||
            "This call conflicts with an existing meeting or call. Do you want to proceed?"
        );
      } else {
        setAlertMessage(
          error?.response?.data?.message ||
            "Failed to save call. Please try again."
        );
        setAlertType("error");
        setAlertOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values: any) => {
    const payload = preparePayload(values);
    await saveCall(payload, values);
  };

  useEffect(() => {
    const fetchCreatedByOptions = async () => {
      try {
        const response = await getUserDetails({}, "Create Campaigns");
        const data = response.data.data.data.data;
        const formattedOptions: OwnerOption[] = data.map((user: any) => ({
          value: String(user.id),
          label: `${user.first_name} ${user.last_name}`,
          email: user.email,
          avatar: user.profile_image_url || null,
        }));

        setLeadOptions(formattedOptions);

        // Set default campaign owner
        const currentUserId = getCurrentUser();
        const matchedUser = formattedOptions?.find(
          (opt) => opt.value === String(currentUserId?.id)
        );
        if (matchedUser) {
          setFormInitialValues((prev: any) => ({
            ...prev,
            callOwner: matchedUser.value,
            relatedTo: campaignDetails?.name || "",
          }));
        }
      } catch (error: any) {
        setAlertMessage(
          error?.response?.data?.message ||
            "Something went wrong please try again."
        );
        setAlertType("error");
        setAlertOpen(true);
        setLoading(false);
      }
    };

    fetchCreatedByOptions();
  }, [campaignDetails]);

  const roundToNext15Min = (time: dayjs.Dayjs) => {
    const minutes = time.minute();
    const remainder = 15 - (minutes % 15);
    return time.add(remainder, "minute").startOf("minute");
  };

  const getMinSelectableTime = (values: any) => {
    const now = dayjs();
    const selectedDate = dayjs(values?.callStartDate);

    if (selectedDate.isSame(now, "day")) {
      const rounded = roundToNext15Min(now);

      // Return a Day.js object with only time info (arbitrary fixed date)
      return dayjs()
        .startOf("day")
        .hour(rounded.hour())
        .minute(rounded.minute())
        .second(0);
    }

    return dayjs().startOf("day");
  };

  const getMaxSelectableTime = (values: any) => {
    if (dayjs(values.callStartDate).isSame(dayjs(), "day")) {
      return dayjs(); // restrict to current time
    }
    return dayjs().hour(23).minute(59);
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <>
      <Dialog
        open={open}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              width: isMobile ? "90%" : "650px", // 90% width on mobile
              minWidth: isMobile ? "auto" : "640px",
              position: isMobile ? "fixed" : "absolute",
              top: 0,
              margin: 0,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 1,
            paddingLeft: 3,
            borderBottom: "1px solid #E1E1E1",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold !important" }} // You can also use 700 or 'bold'
          >
            {title}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Formik
          enableReinitialize
          initialValues={computedInitialValues}
          validationSchema={CALL_DIALOG_VALIDATION_SCHEMA}
          onSubmit={(values) => handleSave(values)}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            setFieldTouched,
          }) => {
            return (
              <Form>
                <div className="task-dialog-container task-dialog-call-container">
                  <DialogContent>
                    <Grid container spacing={2}>
                      <Typography className="call-info-header">
                        Call Information
                      </Typography>
                      {/* Call For */}
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={6}>
                          <Typography width={isMobile ? 100 : 200}>
                            Call For
                          </Typography>
                          <Box flex={1} className="call-for-wrapper">
                            <MultiSelectAutocompleteWithProfile
                              fieldName="callFor"
                              options={customerOptions}
                              placeholder="Select contact"
                              selectedValues={values.callFor || []}
                              onChange={(val) => setFieldValue("callFor", val)}
                              noResultsTxt="No contacts found"
                            />
                            {errors.callFor &&
                              touched?.callFor &&
                              typeof errors.callFor === "string" && (
                                <div className="error-message">
                                  {errors.callFor}
                                </div>
                              )}
                          </Box>
                        </Box>
                      </Grid>

                      {/* Related To */}
                      <Grid item xs={12}>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={6}
                          className="relatedTo-input-component"
                        >
                          <Typography width={isMobile ? 100 : 200}>
                            Related to
                          </Typography>
                          <Box flex={1} className="related-to-container">
                            <TextInputComponent
                              type="text"
                              fieldName="relatedTo"
                              optional={false}
                              disabled={true}
                              value={values.relatedTo}
                              maxlength="200"
                            />
                          </Box>
                        </Box>
                      </Grid>

                      {/* Call Type */}
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={6}>
                          <Typography width={isMobile ? 100 : 200}>
                            Call Type
                          </Typography>
                          <Box
                            flex={1}
                            className="call-type-input static-field"
                          >
                            {!isLogCall && (
                              <>
                                <TextInputComponent
                                  type="text"
                                  fieldName="callType"
                                  placeholder="Call Type"
                                  optional={false}
                                  disabled={true}
                                  value={values.callType}
                                  // onChange={handleChange}
                                  maxlength="200"
                                  isLocked={isLogCall ? false : true} // Locking the field
                                />
                                {!isLogCall && (
                                  <Box className="lock-icon-wrapper">
                                    <img
                                      src={lockIcon}
                                      alt="lock icon"
                                      style={{ width: 20, height: 20 }}
                                    />
                                  </Box>
                                )}
                                {<></>}
                              </>
                            )}
                            {isLogCall && (
                              <Box flex={1}>
                                <SelectInputComponent
                                  fieldName="callType"
                                  value={values.callType}
                                  onChange={(e: any) => {
                                    setFieldValue("callType", e);
                                  }}
                                  options={callTypeOptions}
                                  optional={true}
                                  isShowError={!values.callType}
                                  placeholder="Select Call Type"
                                  // disabled={initialCall?.id ? true : false}
                                  // notEditable={initialCall?.id ? true : false}
                                />
                                {/* {initialCall?.id && <Box className="lock-icon-wrapper">
                                  <img
                                    src={lockIcon}
                                    alt="lock icon"
                                    style={{ width: 20, height: 20 }}
                                  />
                                </Box>} */}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Grid>

                      {/* Outgoing Call Status */}
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={6}>
                          <Typography width={isMobile ? 100 : 200}>
                            Outgoing Call Status
                          </Typography>
                          <Box flex={1} className="static-field">
                            <TextInputComponent
                              type="text"
                              fieldName="outGoingCallStatus"
                              optional={false}
                              disabled={true}
                              value={values.outGoingCallStatus}
                              maxlength="200"
                            />
                            <Box className="lock-icon-wrapper">
                              <img
                                src={lockIcon}
                                alt="lock icon"
                                style={{ width: 20, height: 20 }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Grid>

                      {/* Call Start Time */}
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={6}>
                          <Typography width={isMobile ? 100 : 200}>
                            Call Start Time
                          </Typography>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Box flex={1} className="call-start-time-wrapper">
                              <Box className="call-start-time-picker">
                                <DatePickerComponent
                                  label="date"
                                  fieldName="callStartDate"
                                  value={values?.callStartDate || null}
                                  onChange={(date) =>
                                    setFieldValue("callStartDate", date)
                                  }
                                  minDate={undefined}
                                  maxDate={dayjs()}
                                  optional={true}
                                  isLabel={false}
                                  disabled={initialCall?.id ? true : false}
                                />
                              </Box>
                              <Box className="call-start-time-picker">
                                <TimePicker
                                  label="Select Time"
                                  value={values.callStartTime}
                                  onChange={(newTime) =>
                                    setFieldValue("callStartTime", newTime)
                                  }
                                  minTime={dayjs().startOf("day")}
                                  maxTime={getMaxSelectableTime(values)}
                                  // shouldDisableTime={(timeValue, view) => {
                                  //   const minTime = getMinSelectableTime(values);

                                  //   if (dayjs(values.callStartDate).isSame(dayjs(), "day")) {
                                  //     if (view === "hours") {
                                  //       return timeValue.hour() < minTime.hour();
                                  //     }

                                  //     if (view === "minutes") {
                                  //       const selectedHour = values.callStartTime?.hour() ?? 0;
                                  //       const testTime = dayjs()
                                  //         .hour(selectedHour)
                                  //         .minute(timeValue.minute())
                                  //         .second(0);

                                  //       return testTime.isBefore(minTime);
                                  //     }
                                  //   }

                                  //   return false;
                                  // }}

                                  slotProps={{
                                    textField: {
                                      size: "small",
                                      error: Boolean(
                                        touched.callStartTime &&
                                          errors.callStartTime
                                      ),
                                      helperText:
                                        touched.callStartTime &&
                                        errors.callStartTime
                                          ? errors.callStartTime
                                          : " ",
                                      InputProps: {
                                        disableUnderline: true,
                                        style: {
                                          border: "none",
                                        },
                                      },
                                      sx: {
                                        ".MuiOutlinedInput-notchedOutline": {
                                          border: "none", // remove border
                                        },
                                        ".MuiInputBase-input": {
                                          padding: 0,
                                        },
                                        // Hide label *only when floating/shrink*
                                        "& label.MuiInputLabel-shrink": {
                                          display: "none",
                                        },
                                      },
                                    },
                                  }}
                                  disabled={initialCall?.id ? true : false}
                                />
                              </Box>
                              {initialCall?.id && (
                                <Box className="lock-icon-wrapper">
                                  <img
                                    src={lockIcon}
                                    alt="lock icon"
                                    style={{ width: 20, height: 20 }}
                                  />
                                </Box>
                              )}
                            </Box>
                          </LocalizationProvider>
                        </Box>
                      </Grid>

                      {/* Call Duration */}
                      {(isLogCall ||
                        initialCall?.call_duration ||
                        markCallAsCompletedOpen) && (
                        <Grid item xs={12} spacing={2}>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={6}
                            className="call-duration-campaign-input-wrapper"
                          >
                            <Typography width={isMobile ? 100 : 200}>
                              Call Duration
                            </Typography>
                            <CallDurationInput
                              value={
                                values.callDuration ?? {
                                  hh: "",
                                  mm: "00",
                                  ss: "00",
                                }
                              }
                              onChange={(newVal) =>
                                setFieldValue("callDuration", newVal)
                              }
                              error={errors.callDuration}
                              touched={touched.callDuration}
                              setFieldTouched={setFieldTouched}
                            />
                          </Box>
                        </Grid>
                      )}

                      {/* Call Owner */}
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={6}>
                          <Typography width={isMobile ? 100 : 200}>
                            Call Owner
                          </Typography>
                          <Box flex={1}>
                            <SearchableSelectOwner
                              fieldName="callOwner"
                              options={leadOptions}
                              isShowError={!values?.callOwner}
                              placeholder="Select Owner"
                              optional={true}
                              value={
                                leadOptions.find(
                                  (opt) => opt.value === values?.callOwner
                                ) || null
                              }
                              onChange={(
                                selectedOption: OwnerOption | null
                              ) => {
                                if (selectedOption) {
                                  setFieldValue(
                                    "callOwner",
                                    selectedOption.value
                                  ); // saving only the ID
                                } else {
                                  setFieldValue("callOwner", null); // handle null case
                                }
                              }}
                              handleBlur={() => {
                                setFieldTouched("callOwner", true);
                              }}
                            />
                          </Box>
                        </Box>
                      </Grid>

                      {/* Subject */}
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={6}>
                          <Typography width={isMobile ? 100 : 200}>
                            Subject
                          </Typography>
                          <Box flex={1}>
                            <TextInputComponent
                              type="text"
                              fieldName="subject"
                              placeholder="Add subject"
                              optional={false}
                              disabled={false}
                              value={values.subject}
                              onChange={handleChange}
                              maxlength="200"
                            />
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>

                    <Typography className="call-purpose-header">
                      Purpose Of Outgoing Call
                    </Typography>
                    <Grid container spacing={2}>
                      {/* Call Purpose */}
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={6}>
                          <Typography width={isMobile ? 100 : 200}>
                            Call Purpose
                          </Typography>
                          <Box flex={1}>
                            <SelectInputComponent
                              fieldName="priority"
                              value={values?.callPurpose || "None"}
                              onChange={(e: any) => {
                                setFieldValue("callPurpose", e);
                              }}
                              options={CALL_PURPOSE_OPTIONS}
                              optional={true}
                              isShowError={!values.callPurpose}
                              placeholder="Select Call Purpose"
                            />
                          </Box>
                        </Box>
                      </Grid>
                      {/* Call Agenda */}
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={6}>
                          <Typography width={isMobile ? 100 : 200}>
                            Call Agenda
                          </Typography>
                          <Box flex={1}>
                            <TextInputComponent
                              type="text"
                              fieldName="callAgenda"
                              placeholder="Add call agenda"
                              optional={false}
                              disabled={false}
                              value={values.callAgenda}
                              onChange={handleChange}
                              maxlength="200"
                            />
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>

                    {(isLogCall || initialCall?.call_duration) && (
                      <>
                        <Typography className="call-purpose-header">
                          Outcome Of Outgoing Call
                        </Typography>
                        <Grid container spacing={2}>
                          {/* Call Result */}
                          <Grid item xs={12}>
                            <Box display="flex" alignItems="center" gap={6}>
                              <Typography width={isMobile ? 100 : 200}>
                                Call Result
                              </Typography>
                              <Box flex={1}>
                                <SelectInputComponent
                                  fieldName="callResult"
                                  value={values?.callResult || "None"}
                                  onChange={(e: any) => {
                                    setFieldValue("callResult", e);
                                  }}
                                  options={CALL_RESULT_OPTIONS}
                                  optional={true}
                                  isShowError={!values.callPurpose}
                                  placeholder="Select Call Result"
                                />
                              </Box>
                            </Box>
                          </Grid>
                          {/* Call Description */}
                          <Grid item xs={12}>
                            <Box display="flex" alignItems="center" gap={6}>
                              <Typography width={isMobile ? 100 : 200}>
                                Description
                              </Typography>
                              <Box flex={1}>
                                <TextInputComponent
                                  type="text"
                                  fieldName="description"
                                  placeholder="Add description"
                                  optional={false}
                                  disabled={false}
                                  value={values.description}
                                  onChange={handleChange}
                                  maxlength="200"
                                />
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </DialogContent>
                  <DialogActions sx={{ margin: "12px" }}>
                    <button
                      type="button"
                      onClick={onClose}
                      className="cancel-button"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="cancel-button next-button-wrapper"
                      disabled={loading || isFutureCallStart}
                    >
                      Save
                    </button>
                  </DialogActions>
                </div>
              </Form>
            );
          }}
        </Formik>
        <div className="mark-completed">
          <ConfirmationPopup
            open={isConflictWithMeeting}
            onCancel={() => {
              setIsConflictWithMeeting(false);
              setConflictMessage("");
            }}
            onOkay={() => {
              if (pendingPayload) {
                const updatedPayload = {
                  ...pendingPayload,
                  ignoreOverlap: true, // ✅ Add this key when retrying
                };
                saveCall(updatedPayload);
                setIsConflictWithMeeting(false);
                setConflictMessage("");
                setPendingPayload(null);
              }
            }}
            title={conflictMessage}
            note="Are you sure you want to create this call anyway?"
            buttonText="Confirm"
            Icon={ConfirmInfo}
            className="mark-completed-confirmation"
          />
        </div>
      </Dialog>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={handleAlertClose}
      />
    </>
  );
};

export default CommonCallLogDialog;
